import React, { useState, useCallback, useEffect } from 'react';
import { Eye, X, AlertTriangle, CheckCircle, Info, Loader2, ChevronDown, ChevronUp, ExternalLink, Type, ZoomIn, Contrast, MousePointer2, Focus, Volume2, Underline } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';

const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-500/10 border-red-500/20', text: 'text-red-400', badge: 'bg-red-500/20 text-red-400' },
  serious: { bg: 'bg-orange-500/10 border-orange-500/20', text: 'text-orange-400', badge: 'bg-orange-500/20 text-orange-400' },
  moderate: { bg: 'bg-yellow-500/10 border-yellow-500/20', text: 'text-yellow-400', badge: 'bg-yellow-500/20 text-yellow-400' },
  minor: { bg: 'bg-blue-500/10 border-blue-500/20', text: 'text-blue-400', badge: 'bg-blue-500/20 text-blue-400' },
};

function ViolationCard({ violation }) {
  const [open, setOpen] = useState(false);
  const colors = SEVERITY_COLORS[violation.impact] || SEVERITY_COLORS.minor;

  return (
    <div className={`border rounded-lg ${colors.bg} transition-all`} data-testid={`a11y-violation-${violation.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-start gap-3 p-3 text-left">
        <AlertTriangle size={14} className={`${colors.text} mt-0.5 flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${colors.badge}`}>{violation.impact}</span>
            <span className="text-xs font-medium text-txt-primary truncate">{violation.id}</span>
          </div>
          <p className="text-xs text-txt-secondary mt-1 line-clamp-2">{violation.description}</p>
          <p className="text-[10px] text-txt-muted mt-0.5">{violation.nodes?.length || 0} element(s) affected</p>
        </div>
        {open ? <ChevronUp size={14} className="text-txt-muted flex-shrink-0" /> : <ChevronDown size={14} className="text-txt-muted flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <p className="text-xs text-txt-secondary">{violation.help}</p>
          {violation.helpUrl && (
            <a href={violation.helpUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-brand-primary flex items-center gap-1 hover:underline">
              Learn more <ExternalLink size={10} />
            </a>
          )}
          {violation.nodes?.slice(0, 3).map((node, i) => (
            <div key={i} className="bg-black/20 rounded-md p-2 text-[10px] font-mono text-txt-muted break-all">
              {node.html?.slice(0, 200)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const A11Y_TOOLS = [
  { id: 'fontSize', icon: Type, label: 'Text Size' },
  { id: 'contrast', icon: Contrast, label: 'High Contrast' },
  { id: 'links', icon: Underline, label: 'Highlight Links' },
  { id: 'cursor', icon: MousePointer2, label: 'Large Cursor' },
  { id: 'focus', icon: Focus, label: 'Focus Highlight' },
  { id: 'zoom', icon: ZoomIn, label: 'Page Zoom' },
];

export default function AccessibilityButton() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('tools'); // tools | audit
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  
  // Accessibility tool states
  const [fontScale, setFontScale] = useState(100);
  const [highContrast, setHighContrast] = useState(false);
  const [highlightLinks, setHighlightLinks] = useState(false);
  const [largeCursor, setLargeCursor] = useState(false);
  const [focusHighlight, setFocusHighlight] = useState(false);
  const [pageZoom, setPageZoom] = useState(100);

  // Load saved preferences
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('ib_a11y_prefs') || '{}');
      if (saved.fontScale) setFontScale(saved.fontScale);
      if (saved.highContrast) setHighContrast(saved.highContrast);
      if (saved.highlightLinks) setHighlightLinks(saved.highlightLinks);
      if (saved.largeCursor) setLargeCursor(saved.largeCursor);
      if (saved.focusHighlight) setFocusHighlight(saved.focusHighlight);
      if (saved.pageZoom) setPageZoom(saved.pageZoom);
    } catch {}
  }, []);

  // Apply accessibility settings
  useEffect(() => {
    const prefs = { fontScale, highContrast, highlightLinks, largeCursor, focusHighlight, pageZoom };
    localStorage.setItem('ib_a11y_prefs', JSON.stringify(prefs));

    // Font scaling
    document.documentElement.style.fontSize = `${fontScale}%`;
    
    // High contrast mode
    document.documentElement.classList.toggle('a11y-high-contrast', highContrast);
    
    // Highlight links
    document.documentElement.classList.toggle('a11y-highlight-links', highlightLinks);
    
    // Large cursor
    document.documentElement.classList.toggle('a11y-large-cursor', largeCursor);
    
    // Focus highlight
    document.documentElement.classList.toggle('a11y-focus-highlight', focusHighlight);
    
    // Page zoom
    document.body.style.zoom = `${pageZoom}%`;

    return () => {
      document.documentElement.style.fontSize = '';
      document.body.style.zoom = '';
    };
  }, [fontScale, highContrast, highlightLinks, largeCursor, focusHighlight, pageZoom]);

  const resetAll = () => {
    setFontScale(100);
    setHighContrast(false);
    setHighlightLinks(false);
    setLargeCursor(false);
    setFocusHighlight(false);
    setPageZoom(100);
    localStorage.removeItem('ib_a11y_prefs');
  };

  const runAudit = useCallback(async () => {
    setRunning(true);
    setResults(null);
    try {
      const axe = await import('axe-core');
      const res = await axe.default.run(document.body, {
        runOnly: ['wcag2a', 'wcag2aa', 'wcag21a', 'wcag21aa', 'best-practice'],
      });
      setResults(res);
    } catch (e) {
      console.error('Accessibility audit failed:', e);
      setResults({ error: true });
    }
    setRunning(false);
  }, []);

  const totalViolations = results?.violations?.length || 0;
  const totalPasses = results?.passes?.length || 0;
  const score = results ? Math.round((totalPasses / (totalPasses + totalViolations)) * 100) : null;

  const anyActive = fontScale !== 100 || highContrast || highlightLinks || largeCursor || focusHighlight || pageZoom !== 100;

  return (
    <div className="fixed bottom-[5.5rem] right-6 z-[45]" data-testid="accessibility-widget">
      {/* Toggle Button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="accessibility-btn"
        className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
          anyActive 
            ? 'bg-brand-primary text-white border-2 border-brand-primary' 
            : 'bg-bg-card border border-white/10 text-txt-secondary hover:text-brand-primary hover:border-brand-primary/30'
        }`}
        aria-label="Accessibility Tools"
        title={t('a11y_checker') || 'Accessibility Tools'}
      >
        <Eye size={18} />
      </button>

      {/* Panel */}
      {open && (
        <div className="absolute bottom-14 right-0 w-80 sm:w-96 max-h-[75vh] bg-bg-card border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col" data-testid="accessibility-panel">
          {/* Header */}
          <div className="p-4 border-b border-white/5 flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Eye size={16} className="text-brand-primary" />
                <h3 className="font-heading text-sm font-semibold text-txt-primary">
                  {t('a11y_title') || 'Accessibility'}
                </h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 rounded-lg text-txt-muted hover:text-white hover:bg-white/5" data-testid="a11y-close">
                <X size={16} />
              </button>
            </div>
            {/* Tabs */}
            <div className="flex gap-1">
              <button onClick={() => setTab('tools')} data-testid="a11y-tab-tools"
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${tab === 'tools' ? 'bg-brand-primary/10 text-brand-primary' : 'text-txt-muted hover:text-txt-secondary'}`}>
                Accessibility Tools
              </button>
              <button onClick={() => { setTab('audit'); if (!results) runAudit(); }} data-testid="a11y-tab-audit"
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${tab === 'audit' ? 'bg-brand-primary/10 text-brand-primary' : 'text-txt-muted hover:text-txt-secondary'}`}>
                WCAG Audit
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {tab === 'tools' && (
              <>
                {/* Compliance badges */}
                <div className="flex flex-wrap gap-1.5">
                  <span className="px-2 py-0.5 bg-green-500/10 text-green-400 text-[10px] rounded-full font-medium border border-green-500/20">ADA Compliant</span>
                  <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-[10px] rounded-full font-medium border border-blue-500/20">WCAG 2.1 AA</span>
                  <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 text-[10px] rounded-full font-medium border border-purple-500/20">EAA Ready</span>
                </div>

                {/* Text Size */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-txt-primary flex items-center gap-2">
                      <Type size={14} className="text-brand-primary" /> Text Size
                    </label>
                    <span className="text-[10px] text-txt-muted">{fontScale}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setFontScale(Math.max(80, fontScale - 10))} className="w-8 h-8 rounded-lg bg-bg-primary border border-white/10 text-txt-secondary text-sm hover:bg-white/5" data-testid="a11y-font-decrease">A-</button>
                    <input type="range" min="80" max="150" value={fontScale} onChange={e => setFontScale(Number(e.target.value))}
                      className="flex-1 accent-brand-primary h-1" data-testid="a11y-font-slider" />
                    <button onClick={() => setFontScale(Math.min(150, fontScale + 10))} className="w-8 h-8 rounded-lg bg-bg-primary border border-white/10 text-txt-secondary text-sm font-bold hover:bg-white/5" data-testid="a11y-font-increase">A+</button>
                  </div>
                </div>

                {/* Page Zoom */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-txt-primary flex items-center gap-2">
                      <ZoomIn size={14} className="text-brand-primary" /> Page Zoom
                    </label>
                    <span className="text-[10px] text-txt-muted">{pageZoom}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setPageZoom(Math.max(75, pageZoom - 10))} className="w-8 h-8 rounded-lg bg-bg-primary border border-white/10 text-txt-secondary text-xs hover:bg-white/5" data-testid="a11y-zoom-decrease">-</button>
                    <input type="range" min="75" max="150" value={pageZoom} onChange={e => setPageZoom(Number(e.target.value))}
                      className="flex-1 accent-brand-primary h-1" data-testid="a11y-zoom-slider" />
                    <button onClick={() => setPageZoom(Math.min(150, pageZoom + 10))} className="w-8 h-8 rounded-lg bg-bg-primary border border-white/10 text-txt-secondary text-xs hover:bg-white/5" data-testid="a11y-zoom-increase">+</button>
                  </div>
                </div>

                {/* Toggle options */}
                <div className="space-y-2">
                  {[
                    { id: 'highContrast', icon: Contrast, label: 'High Contrast Mode', desc: 'Enhanced color contrast for better readability', state: highContrast, setter: setHighContrast },
                    { id: 'highlightLinks', icon: Underline, label: 'Highlight Links', desc: 'Underline and highlight all clickable links', state: highlightLinks, setter: setHighlightLinks },
                    { id: 'largeCursor', icon: MousePointer2, label: 'Large Cursor', desc: 'Enlarged mouse cursor for easier tracking', state: largeCursor, setter: setLargeCursor },
                    { id: 'focusHighlight', icon: Focus, label: 'Focus Indicators', desc: 'Enhanced focus outlines for keyboard navigation', state: focusHighlight, setter: setFocusHighlight },
                  ].map(item => (
                    <button key={item.id} onClick={() => item.setter(!item.state)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                        item.state ? 'border-brand-primary/30 bg-brand-primary/5' : 'border-white/5 hover:border-white/10'
                      }`}
                      data-testid={`a11y-toggle-${item.id}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        item.state ? 'bg-brand-primary/20 text-brand-primary' : 'bg-bg-primary text-txt-muted'
                      }`}>
                        <item.icon size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-txt-primary">{item.label}</p>
                        <p className="text-[10px] text-txt-muted">{item.desc}</p>
                      </div>
                      <div className={`w-8 h-4 rounded-full transition-all relative ${item.state ? 'bg-brand-primary' : 'bg-white/10'}`}>
                        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${item.state ? 'left-4' : 'left-0.5'}`} />
                      </div>
                    </button>
                  ))}
                </div>

                {/* Reset button */}
                {anyActive && (
                  <button onClick={resetAll} className="w-full py-2 rounded-lg border border-white/10 text-xs text-txt-secondary hover:bg-white/5 transition-all" data-testid="a11y-reset">
                    Reset All Settings
                  </button>
                )}

                {/* Info */}
                <div className="bg-white/[0.02] rounded-lg p-3 text-[10px] text-txt-muted flex items-start gap-2">
                  <Info size={12} className="flex-shrink-0 mt-0.5" />
                  <span>This website is designed to be ADA, WCAG 2.1 AA, and EAA compliant. Use these tools to customize your browsing experience. Settings are saved automatically.</span>
                </div>
              </>
            )}

            {tab === 'audit' && (
              <>
                {running && (
                  <div className="flex flex-col items-center py-8 gap-3">
                    <Loader2 size={24} className="animate-spin text-brand-primary" />
                    <p className="text-sm text-txt-muted">{t('a11y_scanning') || 'Running WCAG audit...'}</p>
                  </div>
                )}

                {results && !results.error && !running && (
                  <>
                    {/* Score */}
                    <div className="text-center">
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
                        score >= 90 ? 'bg-green-500/10 text-green-400' :
                        score >= 70 ? 'bg-yellow-500/10 text-yellow-400' :
                        'bg-red-500/10 text-red-400'
                      }`} data-testid="a11y-score">
                        {score >= 90 ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                        {score}% {t('a11y_compliant') || 'Compliant'}
                      </div>
                      <div className="flex items-center justify-center gap-4 mt-2 text-[10px] text-txt-muted">
                        <span>{totalPasses} {t('a11y_passed') || 'passed'}</span>
                        <span>{totalViolations} {t('a11y_violations') || 'violations'}</span>
                        <span>{results.incomplete?.length || 0} {t('a11y_incomplete') || 'needs review'}</span>
                      </div>
                    </div>

                    {/* Re-scan button */}
                    <button onClick={runAudit} disabled={running} data-testid="a11y-rescan"
                      className="w-full py-2 rounded-lg bg-brand-primary/10 text-brand-primary text-xs font-medium hover:bg-brand-primary/20 transition-all disabled:opacity-50">
                      {running ? 'Scanning...' : 'Re-scan Page'}
                    </button>

                    {/* Violations */}
                    {totalViolations > 0 ? (
                      <div className="space-y-2" data-testid="a11y-violations-list">
                        <h4 className="text-xs font-semibold text-txt-primary flex items-center gap-1">
                          <AlertTriangle size={12} className="text-red-400" />
                          {t('a11y_issues_found') || 'Issues Found'} ({totalViolations})
                        </h4>
                        {results.violations
                          .sort((a, b) => {
                            const order = { critical: 0, serious: 1, moderate: 2, minor: 3 };
                            return (order[a.impact] ?? 4) - (order[b.impact] ?? 4);
                          })
                          .map(v => <ViolationCard key={v.id} violation={v} />)
                        }
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <CheckCircle size={24} className="mx-auto text-green-400 mb-2" />
                        <p className="text-sm text-green-400 font-medium">{t('a11y_no_issues') || 'No accessibility issues found!'}</p>
                      </div>
                    )}
                  </>
                )}

                {results?.error && !running && (
                  <div className="text-center py-8">
                    <AlertTriangle size={24} className="mx-auto text-red-400 mb-2" />
                    <p className="text-sm text-red-400">{t('a11y_error') || 'Audit failed. Please try again.'}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
